<template>
    <main>
        <header>
            <div class="rounded bg-white p-24 shadow flex items-center justify-between gap-8">
                <search
                    class="w-2/5"
                    :autofocus="false"
                    :placeholder="$t('interviews.search_by_candidate')"
                    :value="filter.text_keyword"
                    @input="updateSearch"
                />
                <div v-if="!fetchingInterviews">
                    <strong>
                        {{ totalInterviewCount || 0 }}
                        {{ $t((totalInterviewCount === 1) ? 'general.result' : 'general.results') }}
                    </strong>
                </div>
            </div>
            <div class="my-24 flex items-center gap-12 flex-wrap">
                <action-button
                    icon="calendar-today"
                    :icon-color="filter.today ? 'secondary' : ''"
                    icon-before
                    @click="filter.today = !filter.today; applyFilter(false)"
                >
                    {{ $t('general.today') }}
                </action-button>

                <action-button
                    icon="check"
                    :icon-color="filter.completed ? 'secondary' : ''"
                    icon-before
                    @click="filter.completed = !filter.completed; applyFilter(false)"
                >
                    {{ $t('interview.status.completed') }}
                </action-button>

                <action-button
                    icon="add"
                    @click="toggleFilter"
                >
                    {{ $t('applications.add_filter') }}
                </action-button>

                <popup-modal
                    v-if="showFilter"
                    without-padding
                    @close="toggleFilter"
                >
                    <div class="flex w-768">
                        <div class="flex-col w-1/3 flex-none border-r border-primary-20 rounded-l overflow-hidden">
                            <ul class="list-reset">
                                <li v-for="(label, type) in filterTabs"
                                    :key="'filter-' + type"
                                    :class="[getFilterTabClasses(type), label.icon ? 'flex items-center' : '']"
                                    class="flex items-center gap-8 px-16 py-12 cursor-pointer hover:bg-primary hover:text-white hover:border-primary"
                                    @click="setFilterTab(type)"
                                >
                                    <svg-icon v-if="label.icon" :type="label.icon"/>
                                    <span class="first-letter:uppercase">{{ $t(label.title) }}</span>
                                </li>
                            </ul>
                        </div>
                        <div class="flex flex-col flex-grow pl-32 mr-32 mb-32">
                            <div class="pt-24 mb-32 text-heading-2 font-semibold first-letter:uppercase leading-none text-black">{{ $t('general.filter_on', {subject: $t(filterTabs[filterTabActive].title)}) }}</div>
                            <div class="flex-grow mb-32">
                                <keep-alive>
                                    <component
                                        :is="filterTabActive"
                                        :filter="filter"
                                    >
                                    </component>
                                </keep-alive>
                            </div>

                            <simple-button
                                class="ml-auto"
                                look="primary"
                                display="secondary"
                                icon="replay"
                                @click="applyFilter(true)"
                            >
                                {{ $t('general.apply_filter') }}
                            </simple-button>

                        </div>
                    </div>
                </popup-modal>

                <active-filters
                    ref="activeFilters"
                    :filter="filter"
                    :filterValues="filterValues"
                    @update="applyFilter(false)"
                />
            </div>
        </header>

        <div class="bg-white rounded shadow">
            <pagination :data="pagination"
                        :on-each-side="2"
                        class="p-24"
                        @paginateTo="getInterviews"
            />

            <loading-placeholder
                v-if="fetchingInterviews"
                :amount="10"
                :show-avatar="true"
                size="small"
            />

            <div v-if="interviews.length" id="interviews">
                <interview-list-item
                    v-for="interview in interviews.filter((x) => x.application)"
                    :key="interview.id"
                    :interview="interview"
                />
            </div>

            <empty-state
                v-if="!interviews.length && !fetchingInterviews"
                :description="$t('interviews.empty_state.description')"
                :title="$t('applications.empty_state.title')"
            />

            <pagination :data="pagination"
                        :on-each-side="2"
                        class="p-24"
                        @paginateTo="getInterviews"
            />
        </div>
    </main>
</template>

<script>
import * as InterviewService from '../../services/InterviewService';
import Interview from "../../models/Interview";
import {normalizeJson} from '../../../shared/helpers';
import Pagination from "../navigation/Pagination.vue";
import LoadingPlaceholder from "../ui/LoadingPlaceholder.vue";
import EmptyState from "../ui/EmptyState.vue";
import InterviewListItem from "./InterviewListItem.vue";
import IconToggle from "../forms/IconToggle.vue";
import _clone from "lodash/clone";
import SvgIcon from "../ui/SvgIcon.vue";
import PopupModal from "../ui/PopupModal.vue";
import SimpleButton from "../ui/SimpleButton.vue";
import ActiveFilters from "../interviews/ActiveFilters.vue";
import _isEmpty from "lodash/isEmpty";
import FilterDate from "../applications/FilterDate.vue";
import FilterTags from "../applications/FilterTags.vue";
import FilterJobFunctions from "../applications/FilterJobFunctions.vue";
import FilterStages from "../interviews/FilterStages.vue";
import FilterStatus from "../interviews/FilterStatus.vue";
import FilterSubscribers from "../interviews/FilterSubscribers.vue";
import InterviewStatus from "../../models/InterviewStatus";
import Search from '../ui/Search.vue';
import _debounce from "lodash/debounce";
import {mapActions, mapState} from "pinia";
import {useRepo} from 'pinia-orm';
import {useInterviewStore} from '../../state/modules/interviews';
import {useUsersStore} from '../../state/modules/users';
import ActionButton from "../ui/ActionButton.vue";

export default {
    name: 'InterviewList',
    components: {
        ActionButton,
        ActiveFilters,
        SimpleButton,
        FilterDate,
        FilterTags,
        FilterStatus,
        FilterStages,
        FilterJobFunctions,
        FilterSubscribers,
        PopupModal, SvgIcon, IconToggle, InterviewListItem, EmptyState, LoadingPlaceholder, Pagination,
        Search
    },
    data() {
        return {
            fetchingInterviews: true,
            pagination: {},
            showFilter: false,
            filter: Object.assign({
                date_from: '',
                date_to: '',
                new: false,
                hired: false,
                stages: [],
                users: [],
                tags: [],
                inclusiveTagMode: true,
                today: false,
                completed: false,
                text_keyword: '',
                statuses: [],
                jobfunctions: [],
            }, this.filterValues),
            filterTabActive: '',
            filterTabs: {
                'filter-date': {title: 'general.date.title', icon: 'calendar-today'},
                'filter-status': {title: 'general.status', icon: 'sell'},
                'filter-job-functions': {title: 'general.functions.title', icon: 'format-list-bulleted'},
                'filter-subscribers': {title: 'general.interviewer', icon: 'person'},
                'filter-stages': {title: 'stages.stage', icon: 'check-circle'},
                'filter-tags': {title: 'general.tags.title', icon: 'sell'},
            },
        }
    },
    computed: {
        ...mapState(useInterviewStore, ['filterValues', 'getURLParametersForFilter']),
        interviews() {
            return useRepo(Interview)
                .with('application', (q) => {
                    q.with('stage', (q2) => {
                        q2.with('job');
                    });
                })
                .with('subscriptions', (q) => {
                    q.with('user');
                })
                .with('timeslots')
                .orderBy(this.getOrderField(), 'desc')
                .get();
        },
        totalInterviewCount() {
            return this.pagination.meta.total || 0;
        }
    },
    methods: {
        ...mapActions(useInterviewStore, ['setFilterValues', 'clearInterviews', 'resetState']),
        ...mapActions(useUsersStore, ['fetchUsers']),
        getOrderField() {
            // if (this.filter.today || this.filter.completed) {
            //     return 'starts_at';
            // } else {
                return 'created_at';
            // }
        },
        applyFilter(toggleFilter = false) {
            this.setFilterValues(this.filter);

            if (toggleFilter) {
                this.toggleFilter();
            }

            this.resetState();

            this.getInterviews(1);
        },
        setFilterTab(tab) {
            this.filterTabActive = tab;
        },
        toggleFilter() {
            let visible = !this.showFilter;

            // when modal will be visible:
            // - always toggle first tab
            // - reset filter from state
            if (visible) {
                this.setFilterTab(Object.keys(this.filterTabs)[0]);

                // we may not have a reference between these two variables
                this.filter = _clone(this.filterValues);
            }

            this.showFilter = visible;
        },
        getFilterTabClasses(tab) {
            if (this.filterTabActive === '') {
                this.filterTabActive = tab;
            }

            let classes = [],
                isLastTab = false,
                keys = Object.keys(this.filterTabs)
            ;

            if (this.filterTabActive == tab) {
                classes.push('bg-primary text-white');
            } else {
                classes.push('text-black');
            }

            for (let i in keys) {
                if ((keys[i] === tab) && (i === (keys.length - 1))) {
                    isLastTab = true;
                    break;
                }
            }

            if (!isLastTab) {
                classes.push('border-b');
                if (this.filterTabActive == tab) {
                    classes.push('border-primary');
                } else {
                    classes.push('border-primary-20');
                }
            }

            return classes;
        },
        getInterviews(pageNumber = 1) {
            this.fetchingInterviews = true;

            // set new paginate right away
            if (useRepo(Interview).all().length > 0 && this.pagination.meta) {

                let pageFrom = (this.pagination.meta.per_page * (pageNumber - 1)) + 1;

                this.pagination.meta.current_page = parseInt(pageNumber);
                this.pagination.meta.to = pageFrom + this.pagination.meta.per_page;
                this.pagination.meta.from = pageFrom;
            }

            // clear current interviews before loading in the new ones
            this.clearInterviews();

            this.showLoader = true;

            let urlParams = this.getURLParametersForFilter;

            InterviewService.allInterviews({
                extraParameters: urlParams,
                page: pageNumber
            }).then(async (response) => {
                this.pagination = response;
                return useRepo(Interview).save(normalizeJson(response.data));
            }).then(() => {
                this.fetchingInterviews = false;
            });
        },
        currentPage() {
            const urlParams = new URLSearchParams(window.location.search);
            let page = urlParams.get('page');

            if (!page) {
                page = 1;
            }

            return page;
        },
        fetchStatuses() {
            this.fetchingStatuses = true;

            InterviewService.statuses()
                .then((response) => {
                    return useRepo(InterviewStatus).save(response);
                })
                .then(() => {
                    this.fetchingStatuses = false;
                });
        },
        fetchSubscribers() {
            this.fetchUsers();
        },
        updateSearch(input) {
            this.filter.text_keyword = input;
            this.triggerSearch();
        },
        triggerSearch: _debounce(function () {
            this.applyFilter();
        }, 500),
    },
    watch: {
        '$route'() {
            // react to route changes, check if you go back with browser
            if (this.pagination.meta) {
                if (parseInt(this.currentPage()) !== this.pagination.meta.current_page) {
                    this.getInterviews(this.currentPage());
                }
            }
        },
    },
    created: function () {
        // see if we have to reset the filter values in state
        // this needs to be done on load
        if (_isEmpty(this.filterValues)) {
            this.setFilterValues(this.filter);
        }
    },
    mounted() {
        this.fetchStatuses();
        this.fetchSubscribers();
        this.getInterviews(this.currentPage());
    }
}
</script>
